// TenantContext — fetches the full tenant row from /api/tenant once
// the session is authenticated, exposes the row + a tri-state status
// to consumers via useTenant().
//
// Architecture decisions:
//
//   - Sits inside <AuthProvider> in main.tsx. Reads session via
//     useAuth() — no direct supabase.auth coupling.
//
//   - Three states:
//       loading  — auth is resolved but the fetch hasn't completed.
//       loaded   — fetch succeeded; tenant is the live row.
//       error    — fetch failed OR caller is a platform_admin (no
//                  tenant_id in claims). In both cases we fall back
//                  to PLATFORM_DEFAULT for the tenant value so
//                  consumers don't have to null-check.
//
//   - Does NOT redirect, throw, or sign the user out on error. That
//     decision belongs to the route guard / specific feature, not
//     this provider.
//
//   - Does NOT use TanStack Query yet. The marketing-of-state from
//     fetch isn't worth the dependency this early; a single fetch on
//     auth-state change is sufficient for MVP. When TanStack Query
//     lands as a dependency for richer surfaces, this provider can
//     be re-implemented as a useQuery wrapper.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../lib/auth/auth-context';
import { PLATFORM_DEFAULT, type Tenant } from '../lib/tenant';

export type TenantStatus = 'loading' | 'loaded' | 'error';

export type TenantContextValue = {
  status: TenantStatus;
  tenant: Tenant;
};

const TenantContext = createContext<TenantContextValue | null>(null);

type TenantProviderProps = {
  children: ReactNode;
  // Test seam: lets tests inject a fake fetch without monkeypatching globals.
  fetchImpl?: typeof fetch;
};

export function TenantProvider({
  children,
  fetchImpl,
}: TenantProviderProps): JSX.Element {
  const { isReady, isAuthenticated, session, userType } = useAuth();
  const [state, setState] = useState<TenantContextValue>({
    status: 'loading',
    tenant: PLATFORM_DEFAULT,
  });

  useEffect(() => {
    if (!isReady) {
      setState({ status: 'loading', tenant: PLATFORM_DEFAULT });
      return;
    }

    if (!isAuthenticated || userType === 'platform_admin') {
      // Unauthenticated visitors and platform_admin users both have no
      // tenant row to fetch. Surface as 'error' (the consumer's signal
      // that PLATFORM_DEFAULT is what's actually being shown).
      setState({ status: 'error', tenant: PLATFORM_DEFAULT });
      return;
    }

    const accessToken = session?.access_token;
    if (!accessToken) {
      setState({ status: 'error', tenant: PLATFORM_DEFAULT });
      return;
    }

    const controller = new AbortController();
    setState({ status: 'loading', tenant: PLATFORM_DEFAULT });

    const doFetch = fetchImpl ?? fetch;
    void doFetch('/api/tenant', {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`tenant fetch ${res.status}`);
        const json: Tenant = await res.json();
        setState({ status: 'loaded', tenant: json });
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', tenant: PLATFORM_DEFAULT });
      });

    return () => {
      controller.abort();
    };
  }, [isReady, isAuthenticated, userType, session?.access_token, fetchImpl]);

  const value = useMemo<TenantContextValue>(() => state, [state]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant() must be used inside <TenantProvider>');
  }
  return ctx;
}
